/**
 * R138d · Feb 2026 — Team Allocation page.
 * ------------------------------------------------------------
 * Compact per-user roll-up of who has access to what. One row per
 * workspace member; columns for Department, System role (with hover
 * showing granted permissions), and chips for each Client / Brand /
 * Project they can access — role in parens. Chips are clickable to
 * open the entity, have an `x` for quick removal, and a `+` opens a
 * combined add-access dialog.
 *
 * Filters: Departments, Members, Clients, Brands, Projects — all as
 * sortable / searchable / multi-select. Department cascades into the
 * Member picker options.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PageHeader from "@/components/PageHeader";
import MultiSelect from "@/components/ui/multi-select";
import { apiErrorMessage } from "@/lib/apiError";
import { RefreshCw, X, Plus, LayoutGrid, HelpCircle, Trash2 } from "lucide-react";

const CHIP_STYLES = {
  client: "border-indigo-300 bg-indigo-50 text-indigo-900",
  brand: "border-fuchsia-300 bg-fuchsia-50 text-fuchsia-900",
  project: "border-emerald-300 bg-emerald-50 text-emerald-900",
};

const ENTITY_HREF = {
  client: (id) => `/app/clients/${id}`,
  brand: (id) => `/app/brands/${id}`,
  project: (id) => `/app/projects/${id}`,
};

export default function TeamAllocation() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [workspaceDepartments, setWorkspaceDepartments] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters (all sortable/searchable/multi-select).
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [selectedBrandIds, setSelectedBrandIds] = useState([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [q, setQ] = useState("");

  // Add-access dialog state.
  const [addOpenFor, setAddOpenFor] = useState(null);   // user row being edited

  // R159uu · Feb 2026 — Skip the per-row remove confirmation. Persisted
  // to localStorage so PMs cleaning up hundreds of rows aren't nagged
  // for every click. Bulk-remove (whole row) still prompts because
  // the impact is much bigger.
  const [skipConfirmRemove, setSkipConfirmRemove] = useState(() => {
    try { return localStorage.getItem("co_alloc_skip_confirm") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("co_alloc_skip_confirm", skipConfirmRemove ? "1" : "0"); } catch { /* ignore */ }
  }, [skipConfirmRemove]);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/access/allocation`);
      setRows(data.rows || []);
      setDepartments(data.departments || []);
      setWorkspaceDepartments(data.workspace_departments || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load allocations"));
    } finally { setLoading(false); }
  }, [activeWorkspaceId]);

  useEffect(() => { load(); }, [load]);

  // Derived option lists — union of everything currently on the rows.
  const clientOptions = useMemo(() => {
    const seen = new Map();
    for (const r of rows) for (const c of r.clients || []) if (!seen.has(c.id)) seen.set(c.id, c.name);
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);
  const brandOptions = useMemo(() => {
    const seen = new Map();
    for (const r of rows) for (const b of r.brands || []) if (!seen.has(b.id)) seen.set(b.id, b.name);
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);
  const projectOptions = useMemo(() => {
    const seen = new Map();
    for (const r of rows) for (const p of r.projects || []) if (!seen.has(p.id)) seen.set(p.id, p.name);
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);
  const memberOptions = useMemo(() => {
    const pool = selectedDepts.length > 0
      ? rows.filter((r) => selectedDepts.map((d) => d.toLowerCase()).includes((r.department || "").toLowerCase()))
      : rows;
    return pool
      .filter((r) => (r.name || "").trim() && r.name !== "—")
      .map((r) => ({ value: r.user_id, label: r.name || r.email || r.user_id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows, selectedDepts]);

  const filteredRows = useMemo(() => {
    let out = rows.slice();
    if (selectedDepts.length > 0) {
      const set = new Set(selectedDepts.map((d) => d.toLowerCase()));
      out = out.filter((r) => set.has((r.department || "").toLowerCase()));
    }
    if (selectedMemberIds.length > 0) {
      const set = new Set(selectedMemberIds);
      out = out.filter((r) => set.has(r.user_id));
    }
    if (selectedClientIds.length > 0) {
      const set = new Set(selectedClientIds);
      out = out.filter((r) => (r.clients || []).some((c) => set.has(c.id)));
    }
    if (selectedBrandIds.length > 0) {
      const set = new Set(selectedBrandIds);
      out = out.filter((r) => (r.brands || []).some((b) => set.has(b.id)));
    }
    if (selectedProjectIds.length > 0) {
      const set = new Set(selectedProjectIds);
      out = out.filter((r) => (r.projects || []).some((p) => set.has(p.id)));
    }
    const term = (q || "").trim().toLowerCase();
    if (term) {
      out = out.filter((r) => `${r.name || ""} ${r.email || ""}`.toLowerCase().includes(term));
    }
    return out;
  }, [rows, selectedDepts, selectedMemberIds, selectedClientIds, selectedBrandIds, selectedProjectIds, q]);

  const removeAllocation = async (userId, kind, entityId, entityName) => {
    if (!skipConfirmRemove && !window.confirm(`Remove ${kind} "${entityName}" access from this user?`)) return;
    // R148g · Feb 2026 — Optimistic remove. Previously we `await load()`
    // which re-fetched every user row and reset filters / scroll — felt
    // like a full-page refresh. Now we surgically drop the chip from
    // local state and only fall back to `load()` if the request fails.
    const kindKey = { client: "clients", brand: "brands", project: "projects" }[kind];
    let snapshot = null;
    if (kindKey) {
      setRows((prev) => {
        snapshot = prev;
        return prev.map((r) => {
          if (r.user_id !== userId) return r;
          const next = { ...r };
          next[kindKey] = (r[kindKey] || []).filter((x) => x.id !== entityId);
          next.total_allocations = Math.max(0, (r.total_allocations || 0) - 1);
          return next;
        });
      });
    }
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/team-matrix/bulk-assign`, {
        assignments: [{ user_id: userId, kind, entity_id: entityId, action: "remove" }],
      });
      toast.success("Access removed");
    } catch (e) {
      // Rollback the optimistic change and give the user the real error.
      if (snapshot) setRows(snapshot);
      toast.error(apiErrorMessage(e, "Failed to remove access"));
    }
  };

  // R138e · Feb 2026 — Bulk-remove all allocations for a user row in
  // one shot. Useful when a teammate leaves the team: managers can
  // wipe every client/brand/project grant without hunting for chips.
  const removeAllForRow = async (row) => {
    const total = (row.clients?.length || 0) + (row.brands?.length || 0) + (row.projects?.length || 0);
    if (total === 0) { toast.info(`${row.name || "This user"} has no allocations to remove.`); return; }
    if (!window.confirm(
        `Remove ALL ${total} allocations from ${row.name || row.email || "this user"}?\n\n` +
        `${row.clients?.length || 0} client(s) · ${row.brands?.length || 0} brand(s) · ${row.projects?.length || 0} project(s).\n\n` +
        `This is a one-click cleanup for a departing teammate — not reversible in bulk.`)) return;
    const assignments = [];
    (row.clients || []).forEach((c) => assignments.push({ user_id: row.user_id, kind: "client", entity_id: c.id, action: "remove" }));
    (row.brands || []).forEach((b) => assignments.push({ user_id: row.user_id, kind: "brand", entity_id: b.id, action: "remove" }));
    (row.projects || []).forEach((p) => assignments.push({ user_id: row.user_id, kind: "project", entity_id: p.id, action: "remove" }));
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/team-matrix/bulk-assign`, { assignments });
      toast.success(`Removed ${data.removed || 0} allocation${(data.removed || 0) === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Bulk remove failed"));
    }
  };

  const hasAllocations = filteredRows.some((r) => (r.total_allocations || 0) > 0);
  const hasFilters = selectedDepts.length + selectedMemberIds.length + selectedClientIds.length + selectedBrandIds.length + selectedProjectIds.length > 0 || (q || "").trim();

  return (
    <div className="p-3 lg:p-6 space-y-4">
      <PageHeader
        title="Team Allocation"
        subtitle="Per-user roll-up of Client / Brand / Project access with quick remove and add."
        Icon={LayoutGrid}
        eyebrow="Access"
        actions={
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold text-muted-foreground hover:text-foreground cursor-pointer select-none border border-border h-9 px-2"
                   title="Removes members instantly without a confirm prompt (per-row bulk remove still asks)."
                   data-testid="team-alloc-skip-confirm-wrap">
              <input
                type="checkbox"
                checked={skipConfirmRemove}
                onChange={(e) => setSkipConfirmRemove(e.target.checked)}
                className="h-3.5 w-3.5 accent-primary"
                data-testid="team-alloc-skip-confirm-toggle"
              />
              Skip remove confirm
            </label>
            <Button variant="outline" className="rounded-none h-9" onClick={load} disabled={loading} data-testid="team-alloc-refresh">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        }
      />

      {/* Filter bar */}
      <div className="border border-border bg-muted/40 p-3 flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name / email…"
                className="h-8 text-xs w-52 rounded-none" data-testid="team-alloc-search" />
        {/* R138e · Feb 2026 — Dept picker always renders, sourced
            from the workspace's `departments` collection so admins
            see the affordance even before any member has been
            linked to a dept via department_members. */}
        <MultiSelect options={(workspaceDepartments.length > 0 ? workspaceDepartments : departments).map((d) => ({ value: d, label: d }))}
                     value={selectedDepts} onChange={setSelectedDepts}
                     placeholder="Departments"
                     allLabel={workspaceDepartments.length === 0 ? "No departments configured" : "All departments"}
                     width="w-44" testid="team-alloc-dept-filter" maxChips={1} />
        <MultiSelect options={memberOptions}
                     value={selectedMemberIds} onChange={setSelectedMemberIds}
                     placeholder="Members" allLabel="All members"
                     width="w-52" testid="team-alloc-member-filter" maxChips={1} />
        <MultiSelect options={clientOptions}
                     value={selectedClientIds} onChange={setSelectedClientIds}
                     placeholder="Clients" allLabel="All clients"
                     width="w-44" testid="team-alloc-client-filter" maxChips={1} />
        <MultiSelect options={brandOptions}
                     value={selectedBrandIds} onChange={setSelectedBrandIds}
                     placeholder="Brands" allLabel="All brands"
                     width="w-44" testid="team-alloc-brand-filter" maxChips={1} />
        <MultiSelect options={projectOptions}
                     value={selectedProjectIds} onChange={setSelectedProjectIds}
                     placeholder="Projects" allLabel="All projects"
                     width="w-44" testid="team-alloc-project-filter" maxChips={1} />
        {hasFilters && (
          <button type="button"
                    className="text-[10px] text-muted-foreground underline"
                    onClick={() => { setSelectedDepts([]); setSelectedMemberIds([]); setSelectedClientIds([]); setSelectedBrandIds([]); setSelectedProjectIds([]); setQ(""); }}
                    data-testid="team-alloc-clear-filters">
            Clear
          </button>
        )}
        <span className="text-[10px] text-muted-foreground ml-auto">
          {filteredRows.length} member{filteredRows.length === 1 ? "" : "s"} · {clientOptions.length} clients · {brandOptions.length} brands · {projectOptions.length} projects
        </span>
      </div>

      {/* Table */}
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-[11px]" data-testid="team-alloc-table">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-2 border-r border-border font-semibold uppercase tracking-widest text-[10px] w-48">Department</th>
              <th className="text-left p-2 border-r border-border font-semibold uppercase tracking-widest text-[10px] w-56">Member</th>
              <th className="text-left p-2 border-r border-border font-semibold uppercase tracking-widest text-[10px] w-40">System role</th>
              <th className="text-left p-2 border-r border-border font-semibold uppercase tracking-widest text-[10px]">Clients</th>
              <th className="text-left p-2 border-r border-border font-semibold uppercase tracking-widest text-[10px]">Brands</th>
              <th className="text-left p-2 border-r border-border font-semibold uppercase tracking-widest text-[10px]">Projects</th>
              <th className="text-left p-2 font-semibold uppercase tracking-widest text-[10px] w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && filteredRows.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">No members match the current filters.</td></tr>
            )}
            {!loading && filteredRows.map((r) => (
              <tr key={r.user_id} className="border-t border-border align-top hover:bg-muted/20"
                   data-testid={`team-alloc-row-${r.user_id}`}>
                <td className="p-2 border-r border-border text-muted-foreground">{r.department || "—"}</td>
                <td className="p-2 border-r border-border">
                  <div className="font-semibold" data-testid={`team-alloc-name-${r.user_id}`}>{r.name || "—"}</div>
                  {r.email && <div className="text-[10px] text-muted-foreground">{r.email}</div>}
                </td>
                <td className="p-2 border-r border-border">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button"
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
                              data-testid={`team-alloc-sys-role-${r.user_id}`}>
                        {r.system_role || "—"}
                        <HelpCircle className="w-3 h-3 opacity-60" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[320px] max-h-[60vh] overflow-y-auto p-2 text-[11px]" side="right" align="start">
                      <div className="font-semibold text-xs mb-1">Workspace permissions</div>
                      <div className="text-[10px] text-muted-foreground mb-2">
                        Grants across the whole workspace. Combines with per-entity access roles.
                      </div>
                      {(r.system_role_perms || []).length === 0
                        ? <div className="text-muted-foreground italic">No workspace permissions</div>
                        : (r.system_role_perms || []).map((p) => (
                            <div key={p} className="text-[10px] py-0.5 border-b border-border last:border-0">{p}</div>
                          ))}
                    </PopoverContent>
                  </Popover>
                </td>
                {["clients", "brands", "projects"].map((kindKey) => {
                  const kind = kindKey.slice(0, -1);   // "clients" → "client"
                  const chips = r[kindKey] || [];
                  return (
                    <td key={kindKey} className="p-2 border-r border-border">
                      {chips.length === 0 ? (
                        <span className="text-[10px] text-muted-foreground italic">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1"
                              data-testid={`team-alloc-${kind}-list-${r.user_id}`}>
                          {chips.map((c) => (
                            <AllocationChip
                              key={`${kind}-${c.id}`}
                              chip={c} kind={kind}
                              onRemove={() => removeAllocation(r.user_id, kind, c.id, c.name)}
                              testid={`team-alloc-chip-${kind}-${c.id}-${r.user_id}`}
                            />
                          ))}
                        </div>
                      )}
                    </td>
                  );
                })}
                <td className="p-2">
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline"
                            className="h-7 text-[10px]"
                            onClick={() => setAddOpenFor(r)}
                            title="Add access to more clients / brands / projects"
                            data-testid={`team-alloc-add-btn-${r.user_id}`}>
                      <Plus className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline"
                            className="h-7 text-[10px] text-red-700 border-red-300 hover:bg-red-50 disabled:opacity-40"
                            disabled={(r.total_allocations || 0) === 0}
                            onClick={() => removeAllForRow(r)}
                            title={(r.total_allocations || 0) === 0
                              ? "No allocations to remove"
                              : `Remove all ${r.total_allocations} allocations (departing teammate cleanup)`}
                            data-testid={`team-alloc-remove-all-btn-${r.user_id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!hasAllocations && !loading && (
        <div className="text-[11px] text-muted-foreground">
          Tip: use the Team Access Matrix to bulk-assign members to entities, then come back here for the roll-up view.
        </div>
      )}

      <AddAccessDialog
        open={!!addOpenFor}
        userRow={addOpenFor}
        onOpenChange={(v) => { if (!v) setAddOpenFor(null); }}
        workspaceId={activeWorkspaceId}
        onSaved={() => { setAddOpenFor(null); load(); }}
      />
    </div>
  );
}


function AllocationChip({ chip, kind, onRemove, testid }) {
  const style = CHIP_STYLES[kind] || "border-border bg-muted text-foreground";
  const href = ENTITY_HREF[kind]?.(chip.id);
  const roleLabel = chip.role_label || chip.role || "";
  const perms = chip.permissions || [];
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 border text-[10px] ${style} group`}
              data-testid={testid}
              title={perms.length ? `Grants: ${perms.join(", ")}` : undefined}>
      {href
        ? <Link to={href} className="hover:underline font-semibold">{chip.name}</Link>
        : <span className="font-semibold">{chip.name}</span>}
      {roleLabel && <span className="text-muted-foreground">({roleLabel})</span>}
      <button type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
              className="ml-0.5 opacity-40 group-hover:opacity-100 hover:text-red-700"
              aria-label={`Remove ${kind} access`}
              data-testid={`${testid}-remove`}>
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}


function AddAccessDialog({ open, onOpenChange, userRow, workspaceId, onSaved }) {
  const [clientIds, setClientIds] = useState([]);
  const [brandIds, setBrandIds] = useState([]);
  const [projectIds, setProjectIds] = useState([]);
  const [clientRole, setClientRole] = useState("");
  const [brandRole, setBrandRole] = useState("");
  const [projectRole, setProjectRole] = useState("");
  const [clientOpts, setClientOpts] = useState([]);
  const [brandOpts, setBrandOpts] = useState([]);
  const [projectOpts, setProjectOpts] = useState([]);
  const [allowedRoles, setAllowedRoles] = useState({ client: [], brand: [], project: [] });
  const [defaults, setDefaults] = useState({ client: "member", brand: "member", project: "designer" });
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setClientIds([]); setBrandIds([]); setProjectIds([]);
    setClientRole(""); setBrandRole(""); setProjectRole("");
  };

  const load = useCallback(async () => {
    if (!open || !workspaceId) return;
    try {
      const [entities, rolesRes] = await Promise.all([
        api.get(`/workspaces/${workspaceId}/team-matrix`),
        api.get(`/workspaces/${workspaceId}/team-matrix/allowed-roles`),
      ]);
      const m = entities.data;
      const already = {
        client: new Set((userRow?.clients || []).map((c) => c.id)),
        brand: new Set((userRow?.brands || []).map((b) => b.id)),
        project: new Set((userRow?.projects || []).map((p) => p.id)),
      };
      const _mk = (arr, k) => (arr || [])
        .filter((e) => !already[k].has(e.id))
        .map((e) => ({ value: e.id, label: e.name || e.id }))
        .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
      setClientOpts(_mk(m.entities?.clients, "client"));
      setBrandOpts(_mk(m.entities?.brands, "brand"));
      setProjectOpts(_mk(m.entities?.projects, "project"));
      setAllowedRoles(rolesRes.data?.roles || { client: [], brand: [], project: [] });
      setDefaults(rolesRes.data?.defaults || defaults);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load add-access options"));
    }
  }, [open, workspaceId, userRow, defaults]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!open) reset(); }, [open]);

  const submit = async () => {
    if (!userRow) return;
    const assignments = [];
    for (const cid of clientIds) assignments.push({ user_id: userRow.user_id, kind: "client", entity_id: cid, action: "add", role: clientRole || defaults.client });
    for (const bid of brandIds) assignments.push({ user_id: userRow.user_id, kind: "brand", entity_id: bid, action: "add", role: brandRole || defaults.brand });
    for (const pid of projectIds) assignments.push({ user_id: userRow.user_id, kind: "project", entity_id: pid, action: "add", role: projectRole || defaults.project });
    if (assignments.length === 0) { toast.error("Pick at least one client, brand, or project"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/team-matrix/bulk-assign`, { assignments });
      toast.success(`Added ${data.added || 0} · unchanged ${data.unchanged || 0}${data.errors?.length ? ` · errors ${data.errors.length}` : ""}`);
      onSaved?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Add-access failed"));
    } finally { setBusy(false); }
  };

  if (!userRow) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="team-alloc-add-dialog">
        <DialogHeader>
          <DialogTitle>Add access · {userRow.name}</DialogTitle>
          <DialogDescription className="text-xs">
            Grant this teammate access to more clients, brands, or projects. Pick the role for each group — you can save all three in one shot.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <SectionPicker label="Clients" values={clientIds} onChange={setClientIds} options={clientOpts}
                          role={clientRole || defaults.client} setRole={setClientRole}
                          roleOptions={allowedRoles.client || []} testidBase="team-alloc-add-client" />
          <SectionPicker label="Brands" values={brandIds} onChange={setBrandIds} options={brandOpts}
                          role={brandRole || defaults.brand} setRole={setBrandRole}
                          roleOptions={allowedRoles.brand || []} testidBase="team-alloc-add-brand" />
          <SectionPicker label="Projects" values={projectIds} onChange={setProjectIds} options={projectOpts}
                          role={projectRole || defaults.project} setRole={setProjectRole}
                          roleOptions={allowedRoles.project || []} testidBase="team-alloc-add-project" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="team-alloc-add-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || (clientIds.length + brandIds.length + projectIds.length === 0)}
                  data-testid="team-alloc-add-submit">
            Grant access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SectionPicker({ label, values, onChange, options, role, setRole, roleOptions, testidBase }) {
  return (
    <div className="border border-border p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</span>
        <Badge variant="outline" className="text-[9px]">{values.length} picked</Badge>
        <div className="ml-auto flex items-center gap-1">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">as</span>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="h-7 w-32 text-xs rounded-none" data-testid={`${testidBase}-role`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(roleOptions || []).map((r) => (
                <SelectItem key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <MultiSelect options={options} value={values} onChange={onChange}
                   placeholder={`Pick ${label.toLowerCase()}…`}
                   allLabel={`All ${label.toLowerCase()}`}
                   width="w-full" testid={`${testidBase}-picker`} maxChips={3} />
    </div>
  );
}
